import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ShieldAlert, Unlock, X, AlertTriangle, Calendar, EyeOff, Search, UserX, Plus, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDDMMYYYY } from '../../utils/date';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PER_PAGE = 7;

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

export default function CapacitadorBloqueados() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('intentos');

  // Intentos (3-reprobados)
  const [bloqueados, setBloqueados] = useState([]);
  const [loadingI, setLoadingI] = useState(true);
  const [confirmHabilitar, setConfirmHabilitar] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [pageI, setPageI] = useState(1);
  const [searchBloq, setSearchBloq] = useState('');
  const [historialModal, setHistorialModal] = useState(null); // {rut, nombre} — abre modal con historial
  const [historialData, setHistorialData] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);

  // Vetos confidenciales
  const [vetos, setVetos] = useState([]);
  const [loadingV, setLoadingV] = useState(true);
  const [showVetoModal, setShowVetoModal] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedTrab, setSelectedTrab] = useState(null);
  const [vetoMotivo, setVetoMotivo] = useState('');
  const [confirmDeleteVeto, setConfirmDeleteVeto] = useState(null);
  const searchTimer = useRef(null);

  const fetchBloqueados = useCallback(async () => {
    setLoadingI(true);
    try {
      const res = await axios.get(`${API}/capacitador/bloqueados`, { headers });
      setBloqueados(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar bloqueados');
    }
    setLoadingI(false);
  }, [token]);

  const fetchVetos = useCallback(async () => {
    setLoadingV(true);
    try {
      const res = await axios.get(`${API}/capacitador/vetos`, { headers });
      setVetos(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar vetos');
    }
    setLoadingV(false);
  }, [token]);

  useEffect(() => { fetchBloqueados(); fetchVetos(); }, [fetchBloqueados, fetchVetos]);

  useEffect(() => { setPageI(1); }, [searchBloq]);

  // Buscar trabajadores con debounce
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/capacitador/trabajadores-buscar`, { headers, params: { q: searchQ } });
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchQ, token]);

  const handleHabilitar = async () => {
    if (!confirmHabilitar) return;
    try {
      await axios.post(`${API}/capacitador/habilitar`, {
        rut: confirmHabilitar.rut,
        capacitacion_id: confirmHabilitar.capacitacion_id,
        motivo: motivo.trim(),
      }, { headers });
      toast.success(`${toTitleCase(confirmHabilitar.nombre)} fue habilitado/a. Contador reiniciado a 0/1 para esta capacitación.`);
      setConfirmHabilitar(null); setMotivo('');
      fetchBloqueados();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al habilitar'); }
  };

  const handleCrearVeto = async () => {
    if (!selectedTrab) return toast.error('Selecciona un trabajador');
    if (!vetoMotivo.trim()) return toast.error('Debes ingresar un motivo (registro confidencial)');
    try {
      await axios.post(`${API}/capacitador/vetos`, {
        rut: selectedTrab.rut,
        nombre: selectedTrab.nombre_completo,
        empresa_id: selectedTrab.empresa_id,
        motivo: vetoMotivo.trim(),
      }, { headers });
      toast.success('Veto registrado confidencialmente');
      setShowVetoModal(false); setSelectedTrab(null); setVetoMotivo(''); setSearchQ(''); setSearchResults([]);
      fetchVetos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar veto'); }
  };

  const handleEliminarVeto = async () => {
    if (!confirmDeleteVeto) return;
    try {
      await axios.delete(`${API}/capacitador/vetos/${encodeURIComponent(confirmDeleteVeto.rut)}`, { headers });
      toast.success('Veto eliminado. Trabajador re-habilitado.');
      setConfirmDeleteVeto(null);
      fetchVetos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar veto'); }
  };

  // Abrir modal con historial completo del trabajador
  const openHistorial = async (b) => {
    setHistorialModal({ rut: b.rut, nombre: b.nombre, empresa: b.empresa_nombre });
    setHistorialData(null);
    setLoadingHist(true);
    try {
      const res = await axios.get(`${API}/capacitador/trabajador-historial`, { headers, params: { rut: b.rut } });
      setHistorialData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar historial');
    }
    setLoadingHist(false);
  };

  // Filtro local de bloqueados (nombre, RUT, empresa, capacitación)
  const bloqueadosFiltrados = (() => {
    const q = searchBloq.trim().toLowerCase();
    if (!q) return bloqueados;
    const qNorm = q.replace(/[.\-\s]/g, '');
    return bloqueados.filter(b => {
      const rutNorm = (b.rut || '').replace(/[.\-\s]/g, '').toLowerCase();
      return (b.nombre || '').toLowerCase().includes(q)
        || rutNorm.includes(qNorm)
        || (b.empresa_nombre || '').toLowerCase().includes(q)
        || (b.capacitacion_nombre || '').toLowerCase().includes(q);
    });
  })();

  return (
    <div className="space-y-6" data-testid="capacitador-bloqueados">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Bloqueos</h1>
        </div>
        <p className="text-sm text-slate-500 ml-12">
          Gestión de trabajadores bloqueados por intentos agotados o veto confidencial.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1">
        <button onClick={() => setTab('intentos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'intentos' ? 'border-red-500 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-intentos">
          <span className="inline-flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" /> Por Intentos ({bloqueados.length})</span>
        </button>
        <button onClick={() => setTab('vetos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'vetos' ? 'border-slate-800 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-vetos">
          <span className="inline-flex items-center gap-1.5"><EyeOff className="w-4 h-4" /> Veto Confidencial ({vetos.length})</span>
        </button>
      </div>

      {/* === TAB INTENTOS === */}
      {tab === 'intentos' && (
        <>
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 leading-snug">
              Al habilitar un trabajador, su contador de intentos se reinicia a <strong>0/1</strong> para esa capacitación específica. Si vuelve a reprobar, quedará bloqueado nuevamente.
            </div>
          </div>

          {/* Buscador */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchBloq}
              onChange={(e) => setSearchBloq(e.target.value)}
              placeholder="Buscar por nombre, RUT, empresa o capacitación..."
              className="w-full pl-9 pr-9 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
              data-testid="bloqueados-search"
            />
            {searchBloq && (
              <button type="button" onClick={() => setSearchBloq('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="bloqueados-search-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {loadingI ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" /></div>
          ) : bloqueadosFiltrados.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <Unlock className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600">
                {searchBloq ? 'Sin resultados para tu búsqueda.' : 'No hay trabajadores bloqueados por intentos.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Trabajador</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Empresa</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Capacitación</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Reprobados</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Último Reprobado</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {bloqueadosFiltrados.slice((pageI - 1) * PER_PAGE, pageI * PER_PAGE).map((b, i) => (
                    <tr key={`${b.rut}-${b.capacitacion_id}`}
                      className="border-b border-slate-50 hover:bg-red-50/40 cursor-pointer transition-colors"
                      onClick={() => openHistorial(b)}
                      data-testid={`bloqueado-row-${(pageI - 1) * PER_PAGE + i}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{toTitleCase(b.nombre)}</p>
                        <p className="text-xs text-slate-400 font-mono">{formatRut(b.rut)}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{toTitleCase(b.empresa_nombre || '—')}</td>
                      <td className="px-4 py-3 text-xs text-slate-700">{toTitleCase(b.capacitacion_nombre)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">{b.intentos_consumidos}/{b.max_intentos ?? 3}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDDMMYYYY(b.ultimo_reprobado) || '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); setConfirmHabilitar(b); setMotivo(''); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700"
                          data-testid={`habilitar-btn-${i}`}>
                          <Unlock className="w-3.5 h-3.5" />Habilitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Paginación */}
          {!loadingI && bloqueadosFiltrados.length > PER_PAGE && (
            <Pagination page={pageI} setPage={setPageI} total={bloqueadosFiltrados.length} perPage={PER_PAGE} testid="bloqueados-pagination" />
          )}
        </>
      )}

      {/* === TAB VETOS === */}
      {tab === 'vetos' && (
        <>
          <div className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 flex items-start gap-3">
            <EyeOff className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-200 leading-snug">
              <strong className="text-amber-200">CONFIDENCIAL</strong> — Trabajadores vetados de asignación a <strong>cualquier curso</strong>. Las ESEs y los trabajadores <strong>no saben</strong> que están bloqueados; reciben un mensaje genérico al intentar asignarlos. Solo el Capacitador ve esta lista.
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => { setShowVetoModal(true); setSelectedTrab(null); setVetoMotivo(''); setSearchQ(''); setSearchResults([]); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900"
              data-testid="add-veto-btn">
              <Plus className="w-4 h-4" />Añadir veto
            </button>
          </div>

          {loadingV ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700" /></div>
          ) : vetos.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
              <UserX className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600">No hay trabajadores vetados actualmente.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Trabajador</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Empresa</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Motivo (interno)</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Vetado el</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {vetos.map((v, i) => (
                    <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`veto-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{toTitleCase(v.nombre || '—')}</p>
                        <p className="text-xs text-slate-400 font-mono">{formatRut(v.rut)}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{toTitleCase(v.empresa_nombre || '—')}</td>
                      <td className="px-4 py-3 text-xs text-slate-700 max-w-xs">{v.motivo || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {v.vetado_at ? new Date(v.vetado_at).toLocaleDateString('es-CL', { timeZone: 'America/Santiago' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setConfirmDeleteVeto(v)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-200"
                          data-testid={`remove-veto-btn-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modal Habilitar */}
      {confirmHabilitar && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setConfirmHabilitar(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={ev => ev.stopPropagation()} data-testid="habilitar-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><Unlock className="w-5 h-5 text-emerald-600" />Habilitar Trabajador</h3>
              <button onClick={() => setConfirmHabilitar(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                <p className="font-semibold text-slate-700">{toTitleCase(confirmHabilitar.nombre)} <span className="font-mono text-slate-400">({formatRut(confirmHabilitar.rut)})</span></p>
                <p className="text-slate-500">{toTitleCase(confirmHabilitar.capacitacion_nombre)}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Motivo (opcional)</label>
                <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
                  placeholder="Ej: Capacitación previa adicional, cambio de procedimiento, etc."
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  data-testid="motivo-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setConfirmHabilitar(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
              <button onClick={handleHabilitar} className="px-4 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700" data-testid="confirm-habilitar-btn">
                Confirmar Habilitación
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal Añadir Veto */}
      {showVetoModal && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowVetoModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={ev => ev.stopPropagation()} data-testid="veto-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><EyeOff className="w-5 h-5 text-slate-700" />Veto Confidencial</h3>
              <button onClick={() => setShowVetoModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Buscar trabajador</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <input type="text" value={searchQ} onChange={e => { setSearchQ(e.target.value); setSelectedTrab(null); }}
                    placeholder="Nombre o RUT (mín. 2 caracteres)..."
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm" data-testid="veto-search" />
                </div>
              </div>

              {selectedTrab ? (
                <div className="rounded-lg border-2 border-slate-800 bg-slate-50 px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{toTitleCase(selectedTrab.nombre_completo)}</p>
                      <p className="text-xs font-mono text-slate-500">{formatRut(selectedTrab.rut)}</p>
                      <p className="text-xs text-slate-500 mt-1">{toTitleCase(selectedTrab.empresa_nombre)}</p>
                    </div>
                    <button onClick={() => setSelectedTrab(null)} className="text-xs text-slate-500 hover:text-slate-700">Cambiar</button>
                  </div>
                </div>
              ) : searchQ.length >= 2 ? (
                <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto">
                  {searching ? (
                    <div className="text-xs text-slate-400 p-4 text-center">Buscando...</div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-xs text-slate-400 p-4 text-center">Sin resultados</div>
                  ) : (
                    searchResults.map((w, idx) => (
                      <button key={`${w.rut}-${idx}`} onClick={() => setSelectedTrab(w)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                        data-testid={`veto-result-${idx}`}>
                        <p className="text-sm font-medium text-slate-800">{toTitleCase(w.nombre_completo)}</p>
                        <p className="text-xs text-slate-400 font-mono">{formatRut(w.rut)} · {toTitleCase(w.empresa_nombre)}</p>
                      </button>
                    ))
                  )}
                </div>
              ) : null}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Motivo del veto <span className="text-red-500">*</span></label>
                <textarea value={vetoMotivo} onChange={e => setVetoMotivo(e.target.value)} rows={3}
                  placeholder="Registro interno confidencial. Ej: Falta grave conducta - incidente DD/MM, riesgo de seguridad, etc."
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid="veto-motivo-input" />
                <p className="text-[10px] text-slate-400 mt-1">Este motivo solo es visible para el Capacitador. La ESE recibirá un mensaje genérico.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setShowVetoModal(false)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
              <button onClick={handleCrearVeto} disabled={!selectedTrab || !vetoMotivo.trim()}
                className="px-4 py-1.5 bg-slate-800 text-white text-sm font-medium rounded-md hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-veto-btn">
                Registrar Veto
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal confirmar eliminar veto */}
      {confirmDeleteVeto && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setConfirmDeleteVeto(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={ev => ev.stopPropagation()} data-testid="delete-veto-modal">
            <div className="px-6 py-5">
              <h3 className="text-base font-bold text-slate-900 mb-2">¿Eliminar veto?</h3>
              <p className="text-sm text-slate-600">
                <strong>{toTitleCase(confirmDeleteVeto.nombre)}</strong> volverá a poder ser asignado/a a capacitaciones. ¿Confirmas?
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setConfirmDeleteVeto(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancelar</button>
              <button onClick={handleEliminarVeto} className="px-4 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-testid="confirm-delete-veto-btn">
                Eliminar Veto
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal HISTORIAL del trabajador */}
      {historialModal && createPortal(
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setHistorialModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="historial-modal">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-red-50 to-white">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldAlert className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-bold text-slate-900">Historial de capacitaciones</h3>
                </div>
                <p className="text-sm font-semibold text-slate-800 truncate">{toTitleCase(historialModal.nombre)}</p>
                <p className="text-xs text-slate-500 font-mono">RUT {formatRut(historialModal.rut)} · {toTitleCase(historialModal.empresa || 'Sin empresa')}</p>
              </div>
              <button onClick={() => setHistorialModal(null)}
                className="text-slate-400 hover:text-slate-700 transition-colors"
                data-testid="historial-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-5">
              {loadingHist ? (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" /></div>
              ) : historialData?.encontrado ? (
                <>
                  {/* Resumen por capacitación */}
                  {historialData.resumen?.length > 0 && (
                    <div className="grid md:grid-cols-2 gap-3">
                      {historialData.resumen.map((s, i) => (
                        <div key={i} className={`p-3 rounded-lg border ${s.bloqueado ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50/40'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold text-slate-800">{s.capacitacion}</p>
                            {s.bloqueado ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">BLOQUEADO</span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">ACTIVO</span>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div><p className="text-lg font-black text-slate-700">{s.total}</p><p className="text-[9px] uppercase text-slate-500">Total</p></div>
                            <div><p className="text-lg font-black text-emerald-700">{s.aprobados}</p><p className="text-[9px] uppercase text-slate-500">Aprob.</p></div>
                            <div><p className="text-lg font-black text-red-700">{s.reprobados}</p><p className="text-[9px] uppercase text-slate-500">Reprob.</p></div>
                            <div><p className="text-lg font-black text-slate-500">{s.ausentes}</p><p className="text-[9px] uppercase text-slate-500">Ausent.</p></div>
                          </div>
                          {s.max_intentos !== undefined && (
                            <div className="mt-2 text-[10px] text-slate-500 flex justify-between">
                              <span>Última: {formatDDMMYYYY(s.ultima_fecha) || '—'}</span>
                              <span className="font-semibold">Intentos: {s.intentos_consumidos || 0}/{s.max_intentos}{s.post_habilitacion ? ' (post-habilitación)' : ''}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tabla histórica */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                          <th className="text-left px-3 py-2 font-semibold">Capacitación</th>
                          <th className="text-left px-3 py-2 font-semibold">Horario</th>
                          <th className="text-center px-3 py-2 font-semibold">Asistencia</th>
                          <th className="text-center px-3 py-2 font-semibold">Resultado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historialData.inscripciones.map((r) => (
                          <tr key={r.id} className={`border-t border-slate-100 ${r.resultado === 'reprobado' ? 'bg-red-50/30' : ''}`}>
                            <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{formatDDMMYYYY(r.fecha)}</td>
                            <td className="px-3 py-2 text-slate-700">{r.capacitacion_nombre}</td>
                            <td className="px-3 py-2 text-slate-500">{r.hora_inicio?.slice(0, 5)}-{r.hora_fin?.slice(0, 5)}</td>
                            <td className="px-3 py-2 text-center">
                              {r.asistencia === 'presente' && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">Presente</span>}
                              {r.asistencia === 'ausente' && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">Ausente</span>}
                              {!r.asistencia && <span className="text-slate-400 text-[10px]">—</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {r.resultado === 'aprobado' && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">APROBADO</span>}
                              {r.resultado === 'reprobado' && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">REPROBADO</span>}
                              {!r.resultado && <span className="text-slate-400 text-[10px]">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <UserX className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No se encontró historial para este trabajador.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}

function Pagination({ page, setPage, total, perPage, testid }) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="flex items-center justify-between text-xs text-slate-500 pt-2" data-testid={testid}>
      <span>Mostrando <strong className="text-slate-800">{from}–{to}</strong> de <strong className="text-slate-800">{total}</strong></span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-prev`}>
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="px-3 py-1 text-slate-700 font-semibold">{page} / {totalPages}</span>
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-next`}>
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
